import { useEffect, useState } from 'react'
import { GetRCNOptionsForUser } from '../../../services/UserManagementService'
import { Box, CircularProgress, Typography } from '@mui/material'
import { OrgCard } from './OrgCard'
import type { OrganizationOption } from '../../../models/requestObjects/CommonObjects'
import { handleApiResponse } from '../../../utils/apiHelpers'
import { GetRcnPlanStatuses } from '../../../services/AnnualPlanService'
import { ImplementationPhaseStatuses } from '../../../models/AnnualPlanStatus'
import type { RcnPlanEntry } from '../../../models/requestObjects/AnnualPlanResponseObjects'

interface Props {
  onRCNSelect: (rcn: OrganizationOption) => void
  isImplementationPhase: boolean
}

export const RCNSelection = (props: Props): JSX.Element => {
  const [rcns, setRcns] = useState<OrganizationOption[]>([])
  const [plansByRcnId, setPlansByRcnId] = useState<Record<number, RcnPlanEntry[]> | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const getRcns = async (): Promise<void> => {
      const rcnOrgs = await handleApiResponse(GetRCNOptionsForUser())
      if (rcnOrgs != null) setRcns([...rcnOrgs].sort((a, b) => a.name.localeCompare(b.name)))
      setIsLoading(false)
    }

    const getStatuses = async (): Promise<void> => {
      const summaries = await handleApiResponse(GetRcnPlanStatuses())
      if (summaries != null) {
        setPlansByRcnId(
          summaries.reduce<Record<number, RcnPlanEntry[]>>((acc, summary) => {
            acc[summary.rcnId] = summary.plans
            return acc
          }, {})
        )
      }
    }

    void getRcns()
    void getStatuses()
  }, [])

  const plansForPhase = (plans: RcnPlanEntry[]): RcnPlanEntry[] =>
    plans.filter(plan =>
      ImplementationPhaseStatuses.includes(plan.status) === props.isImplementationPhase)

  return (
    <Box data-testid="rcn-selection" sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '44px'
    }}
    >
      <Typography variant='h4'>RCNs</Typography>
      <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '33px'
      }}
      >
        {isLoading
          ? <CircularProgress />
          : rcns?.map((rcn, index) =>
            <OrgCard
              index={index}
              key={rcn.id}
              name={rcn.name}
              imageKey={rcn.imageKey}
              buttonText={`View ${props.isImplementationPhase ? 'Implementation' : 'Planning'}`}
              onClick={() => { props.onRCNSelect(rcn) }}
              dataTestId='rcn-card'
              buttonDataTestId='view-annual-plans-button'
              plans={plansByRcnId != null ? plansForPhase(plansByRcnId[rcn.id] ?? []) : undefined}
            />)}
      </Box>
    </Box>
  )
}
